import {
  Avatar,
  Box,
  Button,
  IconButton,
  Modal,
  styled,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import EditIcon from "@mui/icons-material/Edit";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChangePassword from "./ChangePassword";
import MfaSettings from "./MfaSettings";
import { useSelector, useDispatch } from "react-redux";
import { useFormik } from "formik";
import axiosInstance from "../../axios/axios";
import { END_POINTS } from "../../shared";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAuth, useTokens } from "../../shared/hooks";
import ImageUploaderWithCrop from "./ImageUploaderWithCrop";
import { setUserLoginDetails } from "../../modules/authentication/login/Login_Details_slice";
import moment from "moment";

const PencilIconButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  bottom: 5,
  right: 5,
  backgroundColor: theme.palette.primary.main,
  border: `2px solid ${theme.palette.background.paper}`,
  width: 28,
  height: 28,
  zIndex: 1,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

interface FormValues {
  firstname: string;
  lastname: string;
}

const getDaysDiff = (expiry: string, timezone: string = "UTC") => {
  const today = moment.tz(timezone).startOf("day");
  const expiryDate = moment.tz(expiry, timezone).startOf("day");
  return expiryDate.diff(today, "days");
};

const Profile = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [image, setImage] = useState<string | null>("");
  const [previousImage, setPreviousImage] = useState<string | null>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isEditClicked, setIsEditClicked] = useState(false);
  const [isChangePasswordClicked, setIsChangePasswordClicked] = useState(false);
  const [fetchedImage, setFetchedImage] = useState<string | null>(null);
  const [days, setDays] = useState<number>(0);
  const [expiryDate, setExpiryDate] = useState("");


  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { logout } = useAuth();
  const { clearTokens } = useTokens();

  const profile_data = useSelector((state: any) => state.UserLoginDetails);
  console.log("Profile Data>>>>>>>>>>>>>>>>>>>>>>: ", profile_data);


  useEffect(() => {
    if (profile_data?.expiry_date) {
      // Extract only the date part
      const dateOnly = new Date(profile_data.expiry_date)
        .toISOString()
        .split("T")[0];
      setExpiryDate(dateOnly);
    }
    const daysDiff:any = getDaysDiff(profile_data.expiry_date, profile_data.timezone || "UTC");
    setDays(daysDiff);
    const fetchUserImage = async () => {
      try {
        const response = await axiosInstance.get(
          "/authentication/user-image?width=100&height=100",
          { responseType: "blob" }
        );
  
        const imageUrl = URL.createObjectURL(response.data);
        setFetchedImage(imageUrl);
      } catch (error) {
        console.error("Failed to fetch profile image:", error);
      }
    };
  
    fetchUserImage();
  }, []);
  

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      toast("Only .jpg, .jpeg, and .png files are allowed", { type: "error" });
      return;
    }

    setPreviousImage(image);
    setSelectedFile(file);
    setIsUploadModalOpen(true);
    event.target.value = "";
  };

  const cancelUpload = () => {
    setImage(previousImage);
    setSelectedFile(null);
    setIsUploadModalOpen(false);
  };

  const handleOnClickSignout = async () => {
    await logout();
    clearTokens();
    navigate("/login");
  };

  const fetchUserDetails = async () => {
    try {
      const response = await axiosInstance.get(END_POINTS.USER_DETAILS);
      if (response.status === 200) {
        const data = response?.data?.data;
        console.log("User Details:>>>>>>>>>>>>>>>>", data);
        if (data) {
          dispatch(setUserLoginDetails({
            email: data?.organization_email,
            firstname: data?.firstname,
            lastname: data?.lastname,
            organization: data?.organization_name,
            role: data?.role_name,
            organization_id: data?.organization_id,
            unique_devices: data?.subscription_features?.unique_devices,
            sso_enabled: data?.sso_enabled || false,
            global_filter:data?.global_filter
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };
  const nameRegex = /^[A-Za-z]+(?: [A-Za-z]+)*$/;
  const formik = useFormik<FormValues>({
    initialValues: {
      firstname: profile_data?.firstname || "",
      lastname: profile_data?.lastname || "",
    },
    validateOnMount: true,
    // validationSchema: Yup.object({
    //   firstname: Yup.string().required("First name is required"),
    //   lastname: Yup.string().required("Last name is required"),
    // }),
    validationSchema: Yup.object({
      firstname: Yup.string()
        .required("First name is required")
        .matches(nameRegex, "Numbers and special characters are not allowed"),
      lastname: Yup.string()
        .required("Last name is required")
        .matches(nameRegex, "Numbers and special characters are not allowed"),
    }),
    onSubmit: async (values) => {
      const request_payload = {
        country_code: "+91",
        first_name: values.firstname,
        last_name: values.lastname,
        phone_number: "1234567890",
      };

      try {
        const response = await axiosInstance.post(END_POINTS.Edit_User, request_payload);
        toast(response.data.message || "Profile updated successfully", {
          type: "success",
          toastId: "profile-update-success",
        });
        await fetchUserDetails();
        setIsEditClicked(false);
      } catch (error) {
        console.error("Error updating user:", error);
        toast("Failed to update profile", {
          type: "error",
          toastId: "profile-update-error",
        });
      }
    },
  });

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Box sx={{ position: "relative", display: "inline-block", width: 120, height: 120, mb: 3 }}>
          <Avatar
            
            src={image || fetchedImage || profile_data?.profile_picture}
            sx={{ width: "100%", height: "100%" }}
          />
          <PencilIconButton as="label">
            <EditIcon fontSize="small" sx={{ color: "white" }} />
            <input type="file" accept=".jpg,.jpeg,.png" hidden onChange={handleImageUpload} />
          </PencilIconButton>
        </Box>
      </Box>

      {isEditClicked ? (
        <form onSubmit={formik.handleSubmit}>
          <TextField
            id="firstname"
            label="First Name"
            fullWidth
            sx={{ 
              mb: 3,
              '& .MuiOutlinedInput-root': {
                backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
                color: isDarkMode ? '#ffffff' : '#000000',
              },
              '& .MuiInputLabel-root': {
                color: isDarkMode ? '#ffffff' : '#000000',
              },
            }}
            // {...formik.getFieldProps("firstname")}
            name="firstname"
            value={formik.values.firstname}
            onBlur={formik.handleBlur}
            onChange={(e) => {
              formik.setFieldTouched("firstname", true, false);
              formik.handleChange(e);
            }}
            error={formik.touched.firstname && Boolean(formik.errors.firstname)}
            helperText={formik.touched.firstname && (formik.errors.firstname || "")}
          />
          <TextField
            id="lastname"
            label="Last Name"
            fullWidth
            sx={{ 
              '& .MuiOutlinedInput-root': {
                backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
                color: isDarkMode ? '#ffffff' : '#000000',
              },
              '& .MuiInputLabel-root': {
                color: isDarkMode ? '#ffffff' : '#000000',
              },
            }}
            // {...formik.getFieldProps("lastname")}
            name="lastname"
            value={formik.values.lastname}
            onBlur={formik.handleBlur}
            onChange={(e) => {
              formik.setFieldTouched("lastname", true, false);
              formik.handleChange(e);
            }}
            error={formik.touched.lastname && Boolean(formik.errors.lastname)}
            helperText={formik.touched.lastname && (formik.errors.lastname || "")}
          />
          <Box sx={{ display: "flex", justifyContent: "space-between", p: 2 }}>
            <Button
              variant="contained"
              sx={{ width: 150 }}
              type="submit"
              // disabled={formik.isSubmitting}
              disabled={formik.isSubmitting || !formik.isValid}
            >
              {formik.isSubmitting ? "Saving..." : "Save"}
            </Button>
            <Button
              variant="outlined"
              sx={{ width: 150 }}
              onClick={() => setIsEditClicked(false)}
              type="button"
              disabled={formik.isSubmitting}
            >
              Cancel
            </Button>
          </Box>
        </form>
      ) : (
        <Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: "18px", mb: 2, color: isDarkMode ? '#ffffff' : '#000000' }}>
              {profile_data?.firstname} {profile_data?.lastname}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 1000,
                fontSize: "14px",
                mb: 3,
                color: isDarkMode ? '#3a2ada' : 'blue',
                cursor: "pointer",
              }}
              onClick={() => setIsEditClicked(true)}
            >
              Edit
            </Typography>
          </Box>
          <Box sx={{ 
            backgroundColor: isDarkMode ? '#2D2D2D' : '#F7F7F9', 
            p: 2, 
            borderRadius: 3, 
            mb: 3,
            color: isDarkMode ? '#ffffff' : '#000000'
          }}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">Email</Typography>
              <Tooltip title={profile_data?.email || ""}>
                <Typography
                  variant="body2"
                  sx={{
                    maxWidth: "70%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: isDarkMode ? '#a0a0a0' : '#000000'
                  }}
                >
                  {profile_data?.email}
                </Typography>
              </Tooltip>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
              <Typography variant="body2">Role</Typography>
               <Tooltip title={profile_data?.role || ""}>
                <Typography
                  variant="body2"
                  sx={{
                    maxWidth: "70%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: isDarkMode ? '#a0a0a0' : '#000000'
                  }}
                >
                  {profile_data?.role}
                </Typography>
              </Tooltip>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
              <Typography variant="body2">Organization</Typography>
               <Tooltip title={profile_data?.organization || ""}>
                <Typography
                  variant="body2"
                  sx={{
                    maxWidth: "70%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: isDarkMode ? '#a0a0a0' : '#000000'
                  }}
                >
                  {profile_data?.organization}
                </Typography>
              </Tooltip>
            </Box>
          </Box>
          {!profile_data?.sso_enabled && (<>
          <MfaSettings ssoEnabled={!!profile_data?.sso_enabled} />
          <Box
            sx={{
              backgroundColor: isDarkMode ? '#2D2D2D' : '#F7F7F9',
              p: 2,
              borderRadius: 3,
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              cursor: "pointer",
              color: isDarkMode ? '#ffffff' : '#000000',
              '&:hover': {
                backgroundColor: isDarkMode ? '#3D3D3D' : '#E7E7E9',
              }
            }}
            
            onClick={() => setIsChangePasswordClicked(true)}
          >
            <Typography variant="body2">Change Password</Typography>
            <ChevronRightIcon sx={{ color: isDarkMode ? '#ffffff' : '#000000' }} />
          </Box>
          </>)}
          <Button
            variant="outlined"
            onClick={handleOnClickSignout}
            sx={{
              color: "#fb4b50",
              border: "1px solid #FB4B50",
              "&:hover": {
                backgroundColor: "#FB4B50",
                color: "#fff",
                fontWeight: "bold",
              },
            }}
          >
            Sign Out
          </Button>
        </Box>
      )}

      {isChangePasswordClicked && (
        <ChangePassword
          isChangePasswordClicked={isChangePasswordClicked}
          setIsChangePasswordClicked={setIsChangePasswordClicked}
        />
      )}

      <Modal open={isUploadModalOpen} onClose={cancelUpload}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: isDarkMode ? '#1e1e1e' : 'white',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            color: isDarkMode ? '#ffffff' : '#000000'
          }}
        >
          {selectedFile && (
            <ImageUploaderWithCrop
              file={selectedFile}
              onClose={cancelUpload}
              isOrg={false}
              onComplete={(previewUrl) => {
                setImage(previewUrl);
                setIsUploadModalOpen(false);
              }}
            />
          )}
        </Box>
      </Modal>
    </>
  );
};

export default Profile;
